/**
 * A seção 2D da análise espacial CPTu (onda 316, R5 e R6; spec 07 §5.7): o canvas pinta uma célula por bloco
 * de pixels, sem suavização — a zona SBTn estimada pela cor da zona, ou a classe da grandeza contínua pelos
 * tokens `--ts-escala-1…7` (o cividis) —, e o SVG por cima desenha os eixos, a linha das bocas e a dos fundos,
 * as estações (a faixa de 6 px com a zona MEDIDA, o código e o afastamento), as isolinhas, os números das
 * zonas numa malha (a cor nunca carrega a informação sozinha, P-UI-15), a faixa do COV e o cursor. A legenda
 * vai escrita embaixo, com a legenda fixa do modelo interpretativo.
 *
 * O cursor anda pelo ponteiro e pelas setas (← → uma coluna, ↑ ↓ uma linha); a leitura da célula sai num
 * `aria-live` só dela: «cota −9,50 m · 0,00 m de A · CPTu01 (medido) · SBTn 7 · areia densa a pedregulhosa ·
 * Ic 1,10». Medido e estimado nunca se confundem: a faixa da estação é a zona da leitura; a célula, a estimada.
 *
 * A ponte de teste `caixa.ponteDaSecao = { celula(distancia, cota), estacoes(), legenda() }` lê o que está
 * pintado (o pixel do canvas) sem caçar elementos. As cores vêm dos tokens (`getComputedStyle`), nunca de hex.
 * O mesmo componente serve ao Portal (`mostrarNaoHomologadas` por propriedade).
 *
 * As sobreposições (pedido do Humberto de 16/10/2026): com `sobreposicoes`, cada vertical ligada ganha, na estação
 * dela, uma faixa de 48 px com o perfil MEDIDO da grandeza escolhida (o traço sobre o papel, com a escala escrita no
 * topo) ou a coluna das zonas SBT/SBTn (as cores do CPeT-IT, com o número da zona escrito onde cabe — P-UI-15), na
 * opacidade pedida. As verticais no mesmo lugar ficam lado a lado. O campo estimado embaixo não muda.
 *
 * O modo planta (Tarefa 316.3, passos 6–10): com `planta` (a `fatiaEmPlanta` da grade do 3D numa cota), o mesmo
 * desenho em planta — x para leste e y para norte, em m, o norte para cima e escrito —, as verticais marcadas na
 * célula delas com a zona MEDIDA nessa cota (o código escrito ao lado) e a mesma legenda. É o caminho do 3D sem WebGL
 * («a seção e a fatia em planta continuam»): a informação não some. Sem linha das bocas, estações nem COV, que são
 * da seção vertical.
 */
import type { JSX } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ZONAS_SBTN } from '../motor';
import { escala, marcas, numeroBr } from './escalas';
import type { MarcaDeHomologacao } from './homologacao';
import {
  LARGURA_DA_SOBREPOSICAO, LEGENDA_DO_ESPACIAL, classeDoValor, classesDaEscala, colunaPrincipal, grandezaDaSecao, isolinhas, leituraNaPlanta,
  leituraNaSecao,
} from './espacialDaCampanha';
import type { GrandezaDaSecao, PlantaNaCota, RespostaDaSecao, SobreposicoesDasVerticais } from './espacialDaCampanha';
import { LegendaSbt } from './LegendaSbt';
import { useLargura } from './useLargura';

/** As margens do desenho, px: o eixo das cotas à esquerda, o das distâncias embaixo, a faixa do COV à direita. */
const ESQUERDA = 56;
const BAIXO = 30;
const TOPO = 26;
const FAIXA_DO_COV = 16;
const FOLGA_DO_COV = 10;
/** A faixa da estação (a zona medida), px. */
const LARGURA_DA_FAIXA = 6;
/** A malha dos números das zonas, px. */
const MALHA_X = 44;
const MALHA_Y = 30;
/** Os limites das classes do COV na faixa (adimensional): a classe escrita na legenda. */
const CLASSES_DO_COV = [0, 0.1, 0.2, 0.3, 0.5, 0.75, 1, 2];

/** O token da classe de uma grandeza contínua. */
const tokenDaClasse = (k: number): string => `--ts-escala-${k}`;

/** Um pixel do canvas, lido pela ponte. */
type Pixel = [number, number, number, number];

/** O que a ponte de teste devolve de uma célula. */
export interface CelulaDaPonte {
  coluna: number; linha: number; pintada: boolean; zona: number; classe: number; cor: Pixel; cov: number;
  valores: Record<string, number>; medida: { codigo: string; zona: number } | null;
}

/** Uma faixa sobreposta como a ponte a lê. */
export interface SobreposicaoDaPonte {
  codigo: string; grandeza: string; x: number; largura: number; opacidade: number;
  /** O que a faixa mostra na cota (a mais próxima leitura): a zona, ou o valor medido. */
  naCota(cota: number): number;
}

/** A ponte de teste da seção. */
export interface PonteDaSecao {
  /** «secao» (a vertical ao longo da linha) ou «planta» (a fatia da grade numa cota). */
  modo: 'secao' | 'planta';
  /** No modo planta, as verticais marcadas: a célula, a zona medida nessa cota e onde ficam no desenho (px). */
  verticais(): { codigo: string; coluna: number; linha: number; zonaMedida: number; x: number; y: number }[];
  celula(distancia: number, cota: number): CelulaDaPonte | null;
  estacoes(): { codigo: string; distancia: number; afastamento: number; x: number }[];
  legenda(): string;
  /** As faixas das verticais desenhadas sobre a seção (as ligadas), na ordem das estações. */
  sobreposicoes(): SobreposicaoDaPonte[];
}

/** A cor de um token lida do CSS do elemento (os dois temas; nunca hex aqui). */
function corDoToken(el: Element, token: string): string {
  return getComputedStyle(el).getPropertyValue(token).trim();
}

/** O índice da coluna (ou linha) mais perto de um valor numa série. */
function maisPerto(serie: ArrayLike<number>, alvo: number): number {
  let melhor = 0;
  for (let i = 1; i < serie.length; i++) if (Math.abs(serie[i] - alvo) < Math.abs(serie[melhor] - alvo)) melhor = i;
  return melhor;
}

/** O texto de um limite da classe, com as casas que o passo pede. */
function limiteBr(v: number, limites: readonly number[]): string {
  const passo = limites.length > 1 ? Math.abs(limites[1] - limites[0]) : 1;
  const casas = Math.max(0, Math.min(4, Math.ceil(-Math.log10(Math.min(passo, Math.abs(v) || passo)) + 1e-9)));
  return numeroBr(v, Number.isInteger(v) && casas <= 2 ? 0 : casas);
}

/** «de 2 a 5 MPa», «≥ 50 MPa», «< 0,2 MPa» — o texto de uma classe. */
function textoDaClasse(k: number, limites: readonly number[], unidade: string, aberta: boolean): string {
  const u = unidade ? ` ${unidade}` : '';
  if (limites.length === 1) return `todas as células: ${numeroBr(limites[0], 2)}${u}`;
  if (k === limites.length - 1 && aberta) return `≥ ${limiteBr(limites[k - 1], limites)}${u}`;
  return `de ${limiteBr(limites[k - 1], limites)} a ${limiteBr(limites[k], limites)}${u}`;
}

/** Uma série de NaN (a planta não tem a linha das bocas, a dos fundos nem o COV). */
const semValor = (n: number): Float64Array => new Float64Array(n).fill(NaN);

/**
 * A planta como a seção a lê: as distâncias são os x (m para leste), as cotas são os y (m para norte, de cima para
 * baixo) — o mesmo desenho, deitado. Sem estações, recorte nem COV.
 */
function respostaDaPlanta(pl: PlantaNaCota, grandeza: GrandezaDaSecao): RespostaDaSecao {
  const nx = pl.x.length, ny = pl.y.length;
  const principal = colunaPrincipal(grandeza);
  const valores: Record<string, Float64Array> = {};
  for (const [c, v] of Object.entries(pl.valores)) valores[c] = Float64Array.from(v);
  return {
    secao: { distancias: Float64Array.from(pl.x), cotas: Float64Array.from(pl.y), valores: valores[principal] ?? semValor(nx * ny), verticais: [], cov: semValor(ny) },
    valores, zonas: pl.zonas, recorte: { boca: semValor(nx), fundo: semValor(nx), de: nx ? pl.x[0] : NaN, ate: nx ? pl.x[nx - 1] : NaN },
    pintar: pl.pintar, estacoes: [], variogramas: [], metodoUsado: 'idw', motivoDoMetodo: null,
    insuficiente: nx && ny ? null : 'a grade não tem fatia nessa cota', noMesmoLugar: [], cov: semValor(ny),
  };
}

export function SecaoEspacial(p: {
  /** A seção ao longo da linha; sem ela, a `planta`. */
  resposta?: RespostaDaSecao;
  /** O modo planta: a fatia da grade do 3D numa cota (o caminho sem WebGL). */
  planta?: PlantaNaCota | null;
  grandeza: GrandezaDaSecao;
  isolinhas?: boolean;
  cursor?: { coluna: number; linha: number } | null;
  aoCursor?(c: { coluna: number; linha: number } | null): void;
  mostrarNaoHomologadas?: boolean;
  altura?: number;
  /** A marca de homologação da grandeza (a tela a dá): escrita na legenda quando não é homologada. */
  marca?: MarcaDeHomologacao | null;
  /** O rótulo da ponta inicial da linha («A» na A–B): «0,00 m de A». */
  rotuloDoInicio?: string;
  /** As faixas das verticais sobre a seção (o pedido do Humberto de 16/10): as que estão em `ligadas`, na opacidade. */
  sobreposicoes?: { dados: SobreposicoesDasVerticais; ligadas: ReadonlySet<string>; opacidade: number } | null;
}): JSX.Element {
  const planta = p.planta ?? null;
  const emPlanta = !!planta;
  const r = useMemo(() => (planta ? respostaDaPlanta(planta, p.grandeza) : p.resposta as RespostaDaSecao), [planta, p.grandeza, p.resposta]);
  const def = grandezaDaSecao(p.grandeza);
  const principal = colunaPrincipal(p.grandeza);
  const [caixa, largura] = useLargura<HTMLDivElement>();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [cursorInterno, setCursorInterno] = useState<{ coluna: number; linha: number } | null>(null);
  const cursor = p.cursor !== undefined ? p.cursor : cursorInterno;
  const nd = r.secao.distancias.length;
  const nc = r.secao.cotas.length;
  /* Na planta, a altura acompanha a proporção da grade (a mesma escala nos dois eixos), entre 240 e 520 px. */
  const larguraLivre = Math.max(0, largura - ESQUERDA - FAIXA_DO_COV - FOLGA_DO_COV - 8);
  const extensaoX = nd > 1 ? Math.abs(r.secao.distancias[nd - 1] - r.secao.distancias[0]) : 0;
  const extensaoY = nc > 1 ? Math.abs(r.secao.cotas[0] - r.secao.cotas[nc - 1]) : 0;
  const altura = p.altura ?? (emPlanta && extensaoX > 0
    ? Math.round(Math.min(520, Math.max(240, (larguraLivre * extensaoY) / extensaoX + TOPO + BAIXO)))
    : 360);
  // A resposta sem a coluna da grandeza (pedida com outra) não se desenha: nunca um campo de outra grandeza.
  const vazia = nd === 0 || nc === 0 || !r.valores[colunaPrincipal(p.grandeza)];
  const comZona = p.grandeza === 'sbtn';

  /* A geometria: o desenho em pixel (nunca esticado por viewBox). */
  const larguraDoDesenho = larguraLivre;
  const alturaDoDesenho = Math.max(0, altura - TOPO - BAIXO);
  const d0 = vazia ? 0 : r.secao.distancias[0];
  const d1 = vazia ? 1 : r.secao.distancias[nd - 1];
  const c0 = vazia ? 1 : r.secao.cotas[0];
  const c1 = vazia ? 0 : r.secao.cotas[nc - 1];
  const ex = useMemo(() => escala('linear', [d0, d1], [ESQUERDA, ESQUERDA + larguraDoDesenho]), [d0, d1, larguraDoDesenho]);
  const ey = useMemo(() => escala('linear', [c0, c1], [TOPO, TOPO + alturaDoDesenho]), [c0, c1, alturaDoDesenho]);
  // A meia célula: a coluna i ocupa [i − ½, i + ½] em distância, a linha l idem em cota.
  const passoX = nd > 1 ? (d1 - d0) / (nd - 1) : 1;
  const passoY = nc > 1 ? (c0 - c1) / (nc - 1) : 1;

  /* As classes da grandeza contínua: pelos valores pintados da coluna principal. */
  const limites = useMemo(() => {
    if (comZona || vazia) return [];
    let lo = Infinity, hi = -Infinity;
    const v = r.valores[principal];
    for (let k = 0; k < v.length; k++) {
      if (!r.pintar[k] || !Number.isFinite(v[k])) continue;
      if (def.escala === 'log' && !(v[k] > 0)) continue;
      if (v[k] < lo) lo = v[k];
      if (v[k] > hi) hi = v[k];
    }
    if (lo === Infinity) return [NaN];
    return classesDaEscala([lo, hi], def.escala);
  }, [r, principal, comZona, vazia, def.escala]);
  const ultimaAberta = useMemo(() => {
    if (comZona || vazia || limites.length < 2) return false;
    let hi = -Infinity;
    const v = r.valores[principal];
    for (let k = 0; k < v.length; k++) if (r.pintar[k] && v[k] > hi) hi = v[k];
    return hi > limites[limites.length - 1] * (1 + 1e-9);
  }, [r, principal, comZona, vazia, limites]);

  /* O canvas: uma célula por pixel, esticado pelo CSS sem suavização. */
  useLayoutEffect(() => {
    const el = canvas.current;
    if (!el || vazia) return;
    el.width = nd;
    el.height = nc;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, nd, nc);
    const cores = new Map<string, string>();
    const cor = (token: string) => {
      let c = cores.get(token);
      if (c === undefined) { c = corDoToken(el, token); cores.set(token, c); }
      return c;
    };
    const v = r.valores[principal];
    for (let l = 0; l < nc; l++) {
      for (let i = 0; i < nd; i++) {
        const k = l * nd + i;
        if (!r.pintar[k]) continue;
        let token: string | null = null;
        if (comZona) {
          const z = r.zonas ? r.zonas[k] : 0;
          if (z >= 1 && z <= 9) token = ZONAS_SBTN[z as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9].token;
        } else {
          const classe = classeDoValor(v[k], limites);
          if (classe > 0) token = tokenDaClasse(classe);
        }
        if (!token) continue;
        const c = cor(token);
        if (!c) continue;
        ctx.fillStyle = c;
        ctx.fillRect(i, l, 1, 1);
      }
    }
  }, [r, nd, nc, vazia, comZona, principal, limites]);

  /* As isolinhas: Ic 2,60 no SBTn; os limites das classes nas contínuas. */
  const segmentos = useMemo(() => {
    if (!p.isolinhas || vazia) return [];
    const niveis = comZona ? [2.6] : limites.slice(1, -1).filter(Number.isFinite);
    const campo = comZona ? r.valores.ic : r.valores[principal];
    if (!campo) return [];
    return niveis.flatMap((n) => isolinhas(campo, nd, nc, n, r.pintar).map((s) => ({ ...s, nivel: n })));
  }, [p.isolinhas, vazia, comZona, limites, r, principal, nd, nc]);

  /* A legenda escrita: as zonas presentes, ou as classes. */
  const zonasPresentes = useMemo(() => {
    const s = new Set<number>();
    if (comZona && r.zonas) for (let k = 0; k < r.zonas.length; k++) if (r.pintar[k] && r.zonas[k] > 0) s.add(r.zonas[k]);
    return s;
  }, [comZona, r]);
  const textoDaLegenda = useMemo(() => {
    const partes: string[] = [];
    if (comZona) {
      partes.push(`Zonas SBTn estimadas: ${[...zonasPresentes].sort((a, b) => a - b).map((z) => `${z} · ${ZONAS_SBTN[z as 1].nome}`).join('; ')}`);
    } else if (limites.length === 1) {
      partes.push(`${def.rotulo}: ${textoDaClasse(1, limites, def.unidade, false)}`);
    } else {
      partes.push(`${def.rotulo} em 7 classes: ${limites.slice(1).map((_, i) => `${i + 1} ${textoDaClasse(i + 1, limites, def.unidade, ultimaAberta)}`).join('; ')}`);
    }
    if (planta) partes.push(`em planta na cota ${numeroBr(planta.cota, 2)} m`);
    partes.push(LEGENDA_DO_ESPACIAL);
    return partes.join(' — ');
  }, [comZona, zonasPresentes, limites, def, ultimaAberta, planta]);

  /* O cursor: pelo ponteiro e pelas setas. */
  const mudarCursor = useCallback((c: { coluna: number; linha: number } | null) => {
    setCursorInterno(c);
    p.aoCursor?.(c);
  }, [p.aoCursor]); // eslint-disable-line react-hooks/exhaustive-deps
  const celulaDoPonteiro = (e: { clientX: number; clientY: number; currentTarget: Element }) => {
    const caixaSvg = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - caixaSvg.left;
    const y = e.clientY - caixaSvg.top;
    if (x < ESQUERDA || x > ESQUERDA + larguraDoDesenho || y < TOPO || y > TOPO + alturaDoDesenho) return null;
    const d = ex.de(x);
    const cota = ey.de(y);
    return { coluna: Math.min(nd - 1, Math.max(0, Math.round((d - d0) / (passoX || 1)))), linha: Math.min(nc - 1, Math.max(0, Math.round((c0 - cota) / (passoY || 1)))) };
  };
  const aoTeclar = (e: React.KeyboardEvent) => {
    if (vazia) return;
    const atual = cursor ?? { coluna: Math.floor(nd / 2), linha: Math.floor(nc / 2) };
    let { coluna, linha } = atual;
    if (e.key === 'ArrowLeft') coluna -= 1;
    else if (e.key === 'ArrowRight') coluna += 1;
    else if (e.key === 'ArrowUp') linha -= 1;
    else if (e.key === 'ArrowDown') linha += 1;
    else if (e.key === 'Escape') { mudarCursor(null); return; }
    else return;
    e.preventDefault();
    mudarCursor({ coluna: Math.min(nd - 1, Math.max(0, coluna)), linha: Math.min(nc - 1, Math.max(0, linha)) });
  };

  /* A leitura da célula do cursor. */
  const leitura = cursor && !vazia ? leituraNaSecao(r, cursor.coluna, cursor.linha) : null;
  const textoDaLeitura = (() => {
    if (planta && cursor && !vazia) {
      const l = leituraNaPlanta(planta, cursor.coluna, cursor.linha);
      const partes = [`${numeroBr(l.x, 1)} m E`, `${numeroBr(l.y, 1)} m N`, `cota ${numeroBr(l.cota, 2)} m`];
      partes.push(l.medida ? `${l.medida.codigo} (medido)` : '(estimado)');
      if (!l.pintada) partes.push('fora do terreno estimado');
      const zona = l.medida ? l.medida.zona : l.zona;
      if (comZona || p.grandeza === 'su') partes.push(zona > 0 ? `SBTn ${zona} · ${ZONAS_SBTN[zona as 1].nome}` : 'SBTn sem classe');
      if (comZona) partes.push(`Ic ${numeroBr(l.valores.ic, 2)}`);
      else {
        const v = l.valores[principal];
        partes.push(`${def.rotulo} ${numeroBr(v, 2)}${def.unidade ? ` ${def.unidade}` : ''}`);
        const classe = classeDoValor(v, limites);
        if (classe > 0 && limites.length > 1) partes.push(`classe ${classe} (${textoDaClasse(classe, limites, def.unidade, ultimaAberta)})`);
      }
      return partes.join(' · ');
    }
    if (!leitura) return '';
    const partes = [`cota ${numeroBr(leitura.cota, 2)} m`, `${numeroBr(leitura.distancia, 2)} m de ${p.rotuloDoInicio ?? 'A'}`];
    if (leitura.medida) partes.push(`${leitura.medida.codigo} (medido)`);
    else partes.push('(estimado)');
    if (!leitura.pintada) partes.push('fora do terreno estimado');
    const zona = leitura.medida ? leitura.medida.zona : leitura.zona;
    if (comZona || p.grandeza === 'su') {
      if (zona > 0) partes.push(`SBTn ${zona}`, ZONAS_SBTN[zona as 1].nome);
      else partes.push('SBTn sem classe');
    }
    if (comZona) partes.push(`Ic ${numeroBr(leitura.valores.ic, 2)}`);
    else {
      const v = leitura.valores[principal];
      partes.push(`${def.rotulo} ${numeroBr(v, 2)}${def.unidade ? ` ${def.unidade}` : ''}`);
      const classe = classeDoValor(v, limites);
      if (classe > 0 && limites.length > 1) partes.push(`classe ${classe} (${textoDaClasse(classe, limites, def.unidade, ultimaAberta)})`);
    }
    const cov = r.cov[cursor!.linha];
    if (Number.isFinite(cov)) partes.push(`COV ${numeroBr(cov, 2)}`);
    return partes.join(' · ');
  })();

  /* As faixas das verticais: na estação de cada uma, a faixa centrada nela (encostada na borda do desenho nas pontas);
     as verticais no mesmo lugar (a mesma estação) lado a lado. */
  const faixasSobrepostas = useMemo(() => {
    const s = p.sobreposicoes;
    if (!s || vazia || larguraDoDesenho <= 0) return [];
    const L = LARGURA_DA_SOBREPOSICAO;
    const porCodigo = new Map(s.dados.verticais.map((v) => [v.codigo, v]));
    const usadas = new Map<number, number>();
    const saida: { codigo: string; x0: number; v: SobreposicoesDasVerticais['verticais'][number] }[] = [];
    for (const e of r.estacoes) {
      const v = porCodigo.get(e.codigo);
      if (!v || !s.ligadas.has(e.codigo)) continue;
      const centro = Math.round(ex.para(e.distancia));
      const ja = usadas.get(centro) ?? 0;
      usadas.set(centro, ja + 1);
      const x0 = Math.min(Math.max(centro - L / 2 + ja * (L + 2), ESQUERDA), ESQUERDA + larguraDoDesenho - L);
      saida.push({ codigo: e.codigo, x0, v });
    }
    return saida;
  }, [p.sobreposicoes, vazia, larguraDoDesenho, r.estacoes, ex]);

  const desenhoDasSobreposicoes = (() => {
    const s = p.sobreposicoes;
    if (!s || faixasSobrepostas.length === 0) return null;
    const L = LARGURA_DA_SOBREPOSICAO;
    const zona = s.dados.grandeza.tipo === 'zona';
    const casas = (a: number, b: number) => (Math.abs(b - a) >= 10 ? 0 : Math.abs(b - a) >= 1 ? 1 : 2);
    return (
      <g className="cptu-secao-sobreposicoes" style={{ opacity: s.opacidade }}
        aria-label={`${s.dados.grandeza.rotulo} medido em ${faixasSobrepostas.map((f) => f.codigo).join(', ')}`}>
        {faixasSobrepostas.map(({ codigo, x0, v }) => {
          const yTopo = ey.para(v.cota);
          const yFundo = ey.para(v.cotas.length ? v.cotas[v.cotas.length - 1] : v.cota);
          let corpo: ReactNode;
          if (zona) {
            corpo = v.trechos.map((t, i) => {
              const y0 = ey.para(t.topo);
              const h = Math.max(0.5, ey.para(t.base) - y0);
              const z = ZONAS_SBTN[t.zona as 1];
              return (
                <g key={i}>
                  <rect x={x0} y={y0} width={L} height={h} fill={z ? `var(${z.token})` : 'none'} />
                  {h >= 14 ? <text className="cptu-texto cptu-secao-sobreposicao-zona" x={x0 + L / 2} y={y0 + h / 2 + 4} textAnchor="middle">{t.zona}</text> : null}
                </g>
              );
            });
          } else {
            // O tipo e os limites da escala escolhida (onda 347): no log, o valor ≤ 0 não tem lugar e abre uma falha; o de
            // fora dos limites vai à borda, e a primeira cota que passa do máximo fixo leva o «▸» [esc.corte].
            const fx = escala(s.dados.tipo ?? 'linear', v.dominio, [x0 + 2, x0 + L - 2]);
            const fixos = s.dados.fixos ?? null;
            let d = '';
            let aberto = false;
            let corte: number | null = null;
            for (let i = 0; i < v.cotas.length; i++) {
              const val = v.valores[i];
              const px = fx.para(val);
              if (!Number.isFinite(val) || !Number.isFinite(px)) { aberto = false; continue; }
              if (fixos && corte === null && val > fixos.max) corte = v.cotas[i];
              const xx = Math.min(Math.max(px, x0), x0 + L);
              d += `${aberto ? 'L' : 'M'}${xx.toFixed(1)},${ey.para(v.cotas[i]).toFixed(1)}`;
              aberto = true;
            }
            corpo = (
              <>
                <rect className="cptu-secao-sobreposicao-fundo" x={x0} y={yTopo} width={L} height={Math.max(1, yFundo - yTopo)} />
                <path className="cptu-secao-sobreposicao-traco" d={d} />
                <text className="cptu-texto cptu-texto-2" x={x0} y={yTopo - 3}>{numeroBr(v.dominio[0], casas(v.dominio[0], v.dominio[1]))}</text>
                <text className="cptu-texto cptu-texto-2" x={x0 + L} y={yTopo - 3} textAnchor="end">{numeroBr(v.dominio[1], casas(v.dominio[0], v.dominio[1]))}</text>
                {corte !== null ? (
                  <text className="cptu-texto cptu-corte" data-cota={corte} x={x0 + L - 1} y={ey.para(corte) + 4} textAnchor="end">▸</text>
                ) : null}
              </>
            );
          }
          return (
            <g key={codigo} className="cptu-secao-sobreposicao" data-codigo={codigo}>
              {corpo}
              <rect className="cptu-secao-sobreposicao-moldura" x={x0} y={yTopo} width={L} height={Math.max(1, yFundo - yTopo)} />
            </g>
          );
        })}
      </g>
    );
  })();

  /* A ponte de teste. */
  useEffect(() => {
    const el = caixa.current;
    if (!el) return undefined;
    const ponte: PonteDaSecao = {
      modo: emPlanta ? 'planta' : 'secao',
      verticais: () => (planta ? planta.verticais.map((w) => ({ ...w, x: ex.para(planta.x[w.coluna]), y: ey.para(planta.y[w.linha]) })) : []),
      celula(distancia, cota) {
        if (vazia) return null;
        const coluna = maisPerto(r.secao.distancias, distancia);
        const linha = maisPerto(r.secao.cotas, cota);
        const k = linha * nd + coluna;
        const l = leituraNaSecao(r, coluna, linha);
        let cor: Pixel = [0, 0, 0, 0];
        const ctx = canvas.current?.getContext('2d');
        if (ctx) { const px = ctx.getImageData(coluna, linha, 1, 1).data; cor = [px[0], px[1], px[2], px[3]]; }
        const v = r.valores[principal][k];
        return {
          coluna, linha, pintada: r.pintar[k] === 1, zona: r.zonas ? r.zonas[k] : 0, classe: comZona ? 0 : classeDoValor(v, limites),
          cor, cov: r.cov[linha], valores: l.valores, medida: l.medida,
        };
      },
      estacoes: () => r.estacoes.map((e) => ({ codigo: e.codigo, distancia: e.distancia, afastamento: e.afastamento, x: ex.para(e.distancia) })),
      legenda: () => textoDaLegenda,
      sobreposicoes: () => faixasSobrepostas.map(({ codigo, x0, v }) => ({
        codigo, grandeza: p.sobreposicoes?.dados.grandeza.chave ?? '', x: x0, largura: LARGURA_DA_SOBREPOSICAO,
        opacidade: p.sobreposicoes?.opacidade ?? 0,
        naCota(cota: number) {
          if (!v.cotas.length) return NaN;
          let melhor = 0;
          for (let i = 1; i < v.cotas.length; i++) if (Math.abs(v.cotas[i] - cota) < Math.abs(v.cotas[melhor] - cota)) melhor = i;
          return v.valores[melhor];
        },
      })),
    };
    Object.defineProperty(el, 'ponteDaSecao', { value: ponte, writable: false, enumerable: false, configurable: true });
    return () => { delete (el as unknown as { ponteDaSecao?: unknown }).ponteDaSecao; };
  }, [caixa, r, vazia, nd, principal, comZona, limites, ex, ey, textoDaLegenda, faixasSobrepostas, p.sobreposicoes, planta, emPlanta]);

  /* Os rótulos das zonas numa malha (a cor nunca sozinha). */
  const rotulosDaMalha = useMemo(() => {
    if (vazia || !comZona || !r.zonas || larguraDoDesenho <= 0) return [];
    const saida: { x: number; y: number; texto: string }[] = [];
    for (let y = TOPO + MALHA_Y / 2; y < TOPO + alturaDoDesenho; y += MALHA_Y) {
      for (let x = ESQUERDA + MALHA_X / 2; x < ESQUERDA + larguraDoDesenho; x += MALHA_X) {
        const coluna = Math.round((ex.de(x) - d0) / (passoX || 1));
        const linha = Math.round((c0 - ey.de(y)) / (passoY || 1));
        if (coluna < 0 || coluna >= nd || linha < 0 || linha >= nc) continue;
        const k = linha * nd + coluna;
        if (!r.pintar[k] || r.zonas[k] <= 0) continue;
        saida.push({ x, y, texto: String(r.zonas[k]) });
      }
    }
    return saida;
  }, [vazia, comZona, r, larguraDoDesenho, alturaDoDesenho, ex, ey, d0, c0, passoX, passoY, nd, nc]);

  /* As faixas medidas das estações: os trechos contíguos da mesma zona. */
  const faixas = useMemo(() => r.estacoes.map((e) => {
    const trechos: { de: number; ate: number; zona: number }[] = [];
    for (let l = 0; l < nc; l++) {
      const z = e.medida[l];
      const ultimo = trechos[trechos.length - 1];
      if (ultimo && ultimo.zona === z && ultimo.ate === l - 1) ultimo.ate = l;
      else trechos.push({ de: l, ate: l, zona: z });
    }
    return { estacao: e, trechos };
  }), [r.estacoes, nc]);

  const yDaLinha = (l: number) => ey.para(r.secao.cotas[l] + passoY / 2);
  const alturaDaLinha = Math.max(1, Math.abs(ey.para(c0 - passoY) - ey.para(c0)));
  const caminho = (serie: Float64Array) => {
    let d = '';
    let aberto = false;
    for (let i = 0; i < nd; i++) {
      const v = serie[i];
      if (!Number.isFinite(v)) { aberto = false; continue; }
      d += `${aberto ? 'L' : 'M'}${ex.para(r.secao.distancias[i]).toFixed(1)},${ey.para(v).toFixed(1)}`;
      aberto = true;
    }
    return d;
  };

  const marcasX = marcas('linear', [d0, d1], 6);
  const marcasY = marcas('linear', [c1, c0], 6);
  const classeDoCov = (v: number) => classeDoValor(v, CLASSES_DO_COV);

  return (
    <div ref={caixa} className="cptu-secao" role="group" tabIndex={0} onKeyDown={aoTeclar}
      aria-label={`${planta ? `Fatia em planta de ${def.rotulo} na cota ${numeroBr(planta.cota, 2)} m` : `Seção ${def.rotulo}`}: setas movem o cursor uma célula, Esc o tira`}>
      {vazia ? (
        <p className="cptu-secao-vazia">{r.insuficiente ?? 'A seção não tem célula para desenhar.'}</p>
      ) : (
        <div className="cptu-secao-desenho" style={{ height: altura }}>
          <canvas ref={canvas} className="cptu-secao-canvas" aria-hidden="true"
            style={{ left: ex.para(d0 - passoX / 2), top: ey.para(c0 + passoY / 2), width: Math.max(1, ex.para(d1 + passoX / 2) - ex.para(d0 - passoX / 2)), height: Math.max(1, ey.para(c1 - passoY / 2) - ey.para(c0 + passoY / 2)) }} />
          <svg className="cptu-secao-svg" width={largura} height={altura} role="img" aria-label={textoDaLegenda}
            onMouseMove={(e) => { const c = celulaDoPonteiro(e); if (c) mudarCursor(c); }}
            onMouseLeave={() => { if (p.cursor === undefined) mudarCursor(null); }}>
            <rect className="cptu-secao-moldura" x={ESQUERDA} y={TOPO} width={larguraDoDesenho} height={alturaDoDesenho} />
            {/* As linhas das bocas e dos fundos: o terreno estimado. */}
            {emPlanta ? null : (
              <>
                <path className="cptu-secao-terreno" d={caminho(r.recorte.boca)} />
                <path className="cptu-secao-terreno fundo" d={caminho(r.recorte.fundo)} />
              </>
            )}
            {/* As isolinhas. */}
            {segmentos.length > 0 && (
              <g className="cptu-secao-isolinhas" aria-label={comZona ? 'Isolinha Ic 2,60' : 'Isolinhas nos limites das classes'}>
                <path d={segmentos.map((s) => `M${ex.para(d0 + s.x1 * passoX).toFixed(1)},${ey.para(c0 - s.y1 * passoY).toFixed(1)}L${ex.para(d0 + s.x2 * passoX).toFixed(1)},${ey.para(c0 - s.y2 * passoY).toFixed(1)}`).join('')} />
              </g>
            )}
            {/* Os números das zonas numa malha. */}
            {rotulosDaMalha.map((m, i) => (
              <text key={i} className="cptu-secao-zona" x={m.x} y={m.y + 4} textAnchor="middle">{m.texto}</text>
            ))}
            {/* As faixas das verticais (o medido de cada uma), por cima do campo estimado e embaixo das estações. */}
            {desenhoDasSobreposicoes}
            {/* As estações: a faixa medida, o código e o afastamento. */}
            {faixas.map(({ estacao: e, trechos }) => {
              const x = ex.para(e.distancia);
              return (
                <g key={`${e.codigo}-${e.distancia}`} className="cptu-secao-estacao" data-codigo={e.codigo}>
                  {trechos.filter((t) => t.zona > 0).map((t) => (
                    <rect key={t.de} x={x - LARGURA_DA_FAIXA / 2} y={yDaLinha(t.de)} width={LARGURA_DA_FAIXA}
                      height={Math.max(1, yDaLinha(t.ate) + alturaDaLinha - yDaLinha(t.de))} fill={`var(${ZONAS_SBTN[t.zona as 1].token})`} />
                  ))}
                  <line className="cptu-secao-estacao-linha" x1={x} x2={x} y1={TOPO} y2={TOPO + alturaDoDesenho} />
                  <text className="cptu-texto cptu-secao-codigo" x={x} y={TOPO - 8} textAnchor="middle">{e.codigo}</text>
                  <text className="cptu-texto cptu-texto-2 cptu-secao-afastamento" x={x} y={TOPO + alturaDoDesenho + 24} textAnchor="middle">
                    {numeroBr(e.distancia, 2)} m{e.afastamento > 0.005 ? ` · ${numeroBr(e.afastamento, 1)} m ao lado` : ''}
                  </text>
                </g>
              );
            })}
            {/* Na planta, as verticais na célula delas, com a zona MEDIDA nessa cota e o código escrito. */}
            {planta ? planta.verticais.map((w) => {
              const cx = ex.para(planta.x[w.coluna]), cy = ey.para(planta.y[w.linha]);
              const z = ZONAS_SBTN[w.zonaMedida as 1];
              return (
                <g key={w.codigo} className="cptu-secao-vertical" data-codigo={w.codigo}>
                  <circle className="cptu-secao-vertical-marca" cx={cx} cy={cy} r={6} fill={z ? `var(${z.token})` : 'none'} />
                  <text className="cptu-texto cptu-secao-codigo" x={cx + 9} y={cy - 6}>{w.codigo}{w.zonaMedida > 0 ? ` · ${w.zonaMedida}` : ''}</text>
                </g>
              );
            }) : null}
            {planta ? <text className="cptu-texto cptu-secao-norte" x={ESQUERDA + larguraDoDesenho} y={TOPO - 8} textAnchor="end">N ↑</text> : null}
            {/* Os eixos. */}
            {marcasY.map((c) => (
              <g key={`c${c}`}>
                <line className="cptu-grade" x1={ESQUERDA - 4} x2={ESQUERDA} y1={ey.para(c)} y2={ey.para(c)} />
                <text className="cptu-texto cptu-texto-2" x={ESQUERDA - 6} y={ey.para(c) + 4} textAnchor="end">{numeroBr(c, 2)}</text>
              </g>
            ))}
            <text className="cptu-texto cptu-texto-2" x={ESQUERDA - 6} y={TOPO - 8} textAnchor="end">{emPlanta ? 'norte (m)' : 'cota (m)'}</text>
            {marcasX.map((d) => (
              <g key={`d${d}`}>
                <line className="cptu-grade" x1={ex.para(d)} x2={ex.para(d)} y1={TOPO + alturaDoDesenho} y2={TOPO + alturaDoDesenho + 4} />
                <text className="cptu-texto cptu-texto-2" x={ex.para(d)} y={TOPO + alturaDoDesenho + 14} textAnchor="middle">{numeroBr(d, 0)}</text>
              </g>
            ))}
            <text className="cptu-texto cptu-texto-2" x={ESQUERDA + larguraDoDesenho} y={altura - 2} textAnchor="end">{emPlanta ? 'leste (m)' : 'distância (m)'}</text>
            {/* A faixa do COV, por cota: a classe pela tonalidade, e o valor na leitura do cursor (só na seção). */}
            {emPlanta ? null : <g className="cptu-secao-cov" aria-label="COV por cota">
              <text className="cptu-texto cptu-texto-2" x={ESQUERDA + larguraDoDesenho + FOLGA_DO_COV + FAIXA_DO_COV / 2} y={TOPO - 8} textAnchor="middle">COV</text>
              {Array.from({ length: nc }, (_, l) => classeDoCov(r.cov[l])).reduce<{ de: number; ate: number; classe: number }[]>((acc, classe, l) => {
                const u = acc[acc.length - 1];
                if (u && u.classe === classe && u.ate === l - 1) u.ate = l; else acc.push({ de: l, ate: l, classe });
                return acc;
              }, []).filter((t) => t.classe > 0).map((t) => (
                <rect key={t.de} x={ESQUERDA + larguraDoDesenho + FOLGA_DO_COV} y={yDaLinha(t.de)} width={FAIXA_DO_COV}
                  height={Math.max(1, yDaLinha(t.ate) + alturaDaLinha - yDaLinha(t.de))} fill={`var(${tokenDaClasse(t.classe)})`} />
              ))}
            </g>}
            {/* O cursor. */}
            {cursor && (
              <g className="cptu-secao-cursor" aria-hidden="true">
                <line x1={ESQUERDA} x2={ESQUERDA + larguraDoDesenho} y1={yDaLinha(cursor.linha) + alturaDaLinha / 2} y2={yDaLinha(cursor.linha) + alturaDaLinha / 2} />
                <line x1={ex.para(r.secao.distancias[cursor.coluna])} x2={ex.para(r.secao.distancias[cursor.coluna])} y1={TOPO} y2={TOPO + alturaDoDesenho} />
              </g>
            )}
          </svg>
        </div>
      )}
      <p className="cptu-secao-leitura" aria-live="polite">{textoDaLeitura}</p>
      <div className="cptu-secao-legenda">
        {comZona ? (
          <LegendaSbt tipo="sbtn" presentes={zonasPresentes} soPresentes titulo="Zonas SBTn estimadas (Robertson 1990)" />
        ) : (
          <figure className="cptu-legenda" aria-label={`Classes de ${def.rotulo}`}>
            <figcaption className="cptu-legenda-titulo">{def.rotulo}{def.unidade ? ` (${def.unidade})` : ''} em {limites.length > 1 ? '7 classes' : 'uma classe'}</figcaption>
            <ul>
              {(limites.length > 1 ? limites.slice(1) : limites).map((_, i) => (
                <li key={i}>
                  <svg className="cptu-amostra-da-zona" width="14" height="14" aria-hidden="true">
                    <rect width="14" height="14" fill={`var(${tokenDaClasse(limites.length > 1 ? i + 1 : 4)})`} />
                  </svg>
                  <span><b>{limites.length > 1 ? i + 1 : '—'}</b> · {textoDaClasse(i + 1, limites, def.unidade, ultimaAberta)}</span>
                </li>
              ))}
            </ul>
          </figure>
        )}
        {emPlanta ? (
          <p className="cptu-secao-nota">
            Em planta na cota {numeroBr(planta!.cota, 2)} m (o norte para cima): o círculo marca a vertical, na cor e com o número da zona MEDIDA nessa cota; entre as verticais, a estimada.
            {p.marca && !p.marca.homologada && (p.mostrarNaoHomologadas ?? true) ? <> <b>▲ não homologada</b>{p.marca.motivo ? ` — ${p.marca.motivo}` : ''}.</> : null}
          </p>
        ) : <p className="cptu-secao-nota">
          <span className="cptu-secao-faixa-medida"><svg className="cptu-amostra-da-zona" width="6" height="14" aria-hidden="true"><rect width="6" height="14" fill="var(--ts-tinta-3)" /></svg> faixa na estação: a zona MEDIDA da vertical; entre as estações, a estimada.</span>
          {' '}COV por cota na faixa à direita, em classes de {CLASSES_DO_COV.slice(1, -1).map((v) => numeroBr(v, 2)).join(' · ')}.
          {p.marca && !p.marca.homologada && (p.mostrarNaoHomologadas ?? true) ? <> <b>▲ não homologada</b>{p.marca.motivo ? ` — ${p.marca.motivo}` : ''}.</> : null}
        </p>}
        {p.sobreposicoes && faixasSobrepostas.length > 0 && (
          p.sobreposicoes.dados.grandeza.tipo === 'zona' ? (
            <LegendaSbt tipo="sbtn" presentes={new Set(p.sobreposicoes.dados.zonasPresentes)} soPresentes
              titulo={`Faixas das verticais: zonas ${p.sobreposicoes.dados.grandeza.rotulo}, medidas`} />
          ) : (
            <p className="cptu-secao-nota">
              Faixas das verticais: {p.sobreposicoes.dados.grandeza.rotulo}{p.sobreposicoes.dados.grandeza.unidade ? ` (${p.sobreposicoes.dados.grandeza.unidade})` : ''} medido,
              {p.sobreposicoes.dados.escala === 'comum' || p.sobreposicoes.dados.grandeza.dominio || p.sobreposicoes.dados.fixos ? ' na mesma escala em todas' : ' na escala de cada vertical'}
              {p.sobreposicoes.dados.tipo === 'log' ? ' (eixo log)' : ''}{p.sobreposicoes.dados.fixos ? ', com os limites fixos (o que passa vai à borda, com «▸»)' : ''}, escrita no topo de cada faixa.
            </p>
          )
        )}
        <p className="cptu-legenda-fixa">{LEGENDA_DO_ESPACIAL}</p>
      </div>
    </div>
  );
}
